import {addDays} from 'date-fns';
import {
  Button,
  ButtonGroup,
  Card,
  FormControl,
  FormField,
  OptionTypeBase,
} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';

import {useFieldArray, UseFormReturn} from 'react-hook-form';

import {last} from 'ramda';
import {isNotNil, isValidDate} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {getApiDateString, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ROUND_TYPE_OPTIONS} from '../constants/roundTypeOptions';
import {getEmptyDiscountValidity} from '../utils/getEmptyDiscountValidity';
import {DiscountApplicationMethodFormValues} from './discountApplicationMethodFormSchema';

interface DiscountValidityListProps extends RequiredTestIdProps {
  control: FormControl<DiscountApplicationMethodFormValues>;
  formApi: UseFormReturn<DiscountApplicationMethodFormValues>;
  roundStepOptions: OptionTypeBase<string>[];
}

export function DiscountValidityList(props: DiscountValidityListProps) {
  const {fields, append, remove} = useFieldArray<DiscountApplicationMethodFormValues>({
    control: props.control,
    name: 'validities',
  });

  const handleCreateNew = () => {
    const lastDate = parseDate(last(props.formApi.getValues('validities'))?.validFrom);
    const newDate = isValidDate(lastDate) ? addDays(lastDate, 1) : undefined;

    append(getEmptyDiscountValidity(newDate));
  };

  return (
    <VStack spacing={4}>
      {fields.map((field, index) => (
        <Card key={field.id} variant="inlineGrey">
          <VStack spacing={4}>
            <Grid columns={3}>
              <FormField
                control={props.control}
                type="choice"
                name={`validities.${index}.roundType`}
                options={ROUND_TYPE_OPTIONS}
                label={i18n.t('entity.accounting.labels.roundingType')}
                isRequired
                data-testid={suffixTestId(`validity-[${index}]-roundType`, props)}
              />
              <FormField
                control={props.control}
                type="choice"
                name={`validities.${index}.roundStep`}
                options={props.roundStepOptions}
                label={i18n.t('entity.accounting.labels.rounding')}
                isRequired
                data-testid={suffixTestId(`validity-[${index}]-roundStep`, props)}
              />
              <FormField
                control={props.control}
                type="date"
                name={`validities.${index}.validFrom`}
                onChange={(date) => {
                  if (isNotNil(date)) {
                    props.formApi.setValue(
                      `validities.${index}.validFrom`,
                      getApiDateString(date),
                      {shouldDirty: true, shouldValidate: true}
                    );
                  }
                }}
                label={i18n.t('entity.accounting.labels.validFrom')}
                isRequired
                data-testid={suffixTestId(`validity-[${index}]-validFrom`, props)}
              />
            </Grid>
            <Show when={fields.length > 1}>
              <HStack>
                <Button
                  title={i18n.t('general.labels.delete')}
                  onClick={() => remove(index)}
                  leftIcon="content/remove_circle"
                  variant="errorLink"
                  data-testid={suffixTestId(`validity-[${index}]-delete`, props)}
                />
              </HStack>
            </Show>
          </VStack>
        </Card>
      ))}
      <ButtonGroup align="left">
        <Button
          title={i18n.t('entity.accounting.actions.addValidity')}
          onClick={handleCreateNew}
          leftIcon="content/add_circle"
          variant="link"
          data-testid={suffixTestId('addValidity', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}
